/*
  File: src/services/calendlyService.js
  Purpose: Service for integrating with Calendly API
  Documentation: https://developer.calendly.com/api-docs
*/

// Get Calendly config from environment
const CALENDLY_API_KEY = process.env.REACT_APP_CALENDLY_API_KEY || '';
const CALENDLY_API_BASE = 'https://api.calendly.com';

/**
 * Fetch scheduled events from Calendly
 * @param {Object} options - Query options
 * @param {string} options.organization - Organization URI
 * @param {string} options.user - User URI
 * @param {Date} options.minStartTime - Minimum start time for events
 * @param {Date} options.maxStartTime - Maximum start time for events
 * @returns {Promise<Array>} Array of scheduled events
 */
export async function fetchCalendlyEvents(options = {}) {
  try {
    // If no API key is configured, return empty array
    if (!CALENDLY_API_KEY) {
      console.error('Calendly API key not configured. Please add REACT_APP_CALENDLY_API_KEY to .env file');
      return [];
    }

    // First, get the current user to obtain their URI
    let userUri = options.user;
    
    if (!userUri && !options.organization && !options.group) {
      console.log('No user/org/group provided, fetching current user...');
      const currentUser = await fetchCurrentUser();
      if (!currentUser || !currentUser.uri) {
        console.error('Could not fetch current user from Calendly API');
        return [];
      }
      userUri = currentUser.uri;
      console.log('Using user URI:', userUri);
    }

    const params = new URLSearchParams();
    
    if (options.organization) params.append('organization', options.organization);
    if (options.group) params.append('group', options.group);
    if (userUri) params.append('user', userUri);
    if (options.minStartTime) params.append('min_start_time', options.minStartTime.toISOString());
    if (options.maxStartTime) params.append('max_start_time', options.maxStartTime.toISOString());
    
    params.append('status', 'active');
    params.append('count', '100');

    const response = await fetch(`${CALENDLY_API_BASE}/scheduled_events?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${CALENDLY_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Calendly API error:', response.status, errorData);
      throw new Error(`Calendly API error: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Fetched ${data.collection?.length || 0} events from Calendly`);
    
    // Transform events to our format
    const events = (data.collection || []).map(event => ({
      id: event.uri?.split('/').pop() || event.uri,
      name: event.name,
      start_time: event.start_time,
      end_time: event.end_time,
      status: event.status,
      event_type: event.location?.type === 'physical' ? 'in_person' : 
                  event.location?.type === 'outbound_call' ? 'phone' : 'video',
      location: event.location?.location || event.location?.join_url || 'TBD',
      uri: event.uri,
      created_at: event.created_at,
      updated_at: event.updated_at,
      // We'll fetch invitee details separately if needed
      invitee_name: 'Guest',
      invitee_email: ''
    }));

    // Optionally fetch invitee details for each event
    const eventsWithInvitees = await Promise.all(
      events.map(async (event) => {
        try {
          const invitees = await fetchEventInvitees(event.id);
          if (invitees && invitees.length > 0) {
            const invitee = invitees[0];
            return {
              ...event,
              invitee_name: invitee.name || 'Guest',
              invitee_email: invitee.email || ''
            };
          }
        } catch (err) {
          console.warn('Could not fetch invitees for event:', event.id);
        }
        return event;
      })
    );

    return eventsWithInvitees;
  } catch (error) {
    console.error('Error fetching Calendly events:', error);
    // Return empty array on error - no mock data
    return [];
  }
}

/**
 * Fetch current user information from Calendly
 * @returns {Promise<Object>} User information
 */
export async function fetchCurrentUser() {
  try {
    if (!CALENDLY_API_KEY) {
      console.warn('No Calendly API key configured');
      return null;
    }

    console.log('Fetching current Calendly user...');
    const response = await fetch(`${CALENDLY_API_BASE}/users/me`, {
      headers: {
        'Authorization': `Bearer ${CALENDLY_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Calendly API error fetching user:', response.status, errorData);
      throw new Error(`Calendly API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Current user fetched successfully:', data.resource?.name);
    return data.resource;
  } catch (error) {
    console.error('Error fetching Calendly user:', error);
    return null;
  }
}

/**
 * Fetch event invitees for a specific event
 * @param {string} eventUuid - Event UUID
 * @returns {Promise<Array>} Array of invitees
 */
export async function fetchEventInvitees(eventUuid) {
  try {
    if (!CALENDLY_API_KEY) {
      return [];
    }

    const response = await fetch(`${CALENDLY_API_BASE}/scheduled_events/${eventUuid}/invitees`, {
      headers: {
        'Authorization': `Bearer ${CALENDLY_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Calendly API error: ${response.status}`);
    }

    const data = await response.json();
    return data.collection || [];
  } catch (error) {
    console.error('Error fetching event invitees:', error);
    return [];
  }
}

export default {
  fetchCalendlyEvents,
  fetchCurrentUser,
  fetchEventInvitees
};
